import { useEffect, useState, useCallback } from "react";
import {
  BookOpen, Plus, Pencil, Trash2, ToggleLeft, ToggleRight,
  Loader2, RefreshCw, Search, AlertTriangle, RotateCcw, Check,
  X, Upload, Download, History,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

const TOPICS = ["all", "auth", "sandbox", "transactions", "webhooks",
                "errors", "settlement", "rate_limits", "other"];

/**
 * Brief 3.4.9 — PayTech FAQ admin page (super_admin only).
 *
 * Lives at /admin/ops/paytech-faq. Surfaces every FAQ entry for
 * review/edit, supports markdown round-trip ingest, and shows a
 * pending-removals banner when a recent re-import flagged entries
 * absent from the source markdown.
 */
export default function AdminPayTechFAQ() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [topics, setTopics] = useState(TOPICS);
  const [pending, setPending] = useState([]);
  const [topic, setTopic] = useState("all");
  const [q, setQ] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (topic !== "all") params.topic = topic;
      if (q) params.q = q;
      if (activeOnly) params.active_only = true;
      const { data } = await api.get("/admin/paytech-faq", { params });
      setEntries(data.entries || []);
      setTopics(["all", ...(data.topics || [])]);
      setPending(data.pending_removals || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load FAQ.");
    } finally {
      setLoading(false);
    }
  }, [topic, q, activeOnly]);

  useEffect(() => { refresh(); }, [refresh]);

  if (user?.role !== "super_admin") {
    return <div data-testid="faq-forbidden" className="text-rose-200">Super-admin only.</div>;
  }

  const onToggle = async (faqId) => {
    try {
      await api.post(`/admin/paytech-faq/${faqId}/toggle-active`);
      toast.success("Toggled.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not toggle.");
    }
  };

  const onDelete = async (faqId) => {
    if (!window.confirm("Soft-delete this entry? It will be flipped to inactive (data retained).")) return;
    try {
      await api.delete(`/admin/paytech-faq/${faqId}`);
      toast.success("Deleted.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete.");
    }
  };

  const onImport = async (markdown) => {
    setImporting(true);
    try {
      const { data } = await api.post(
        "/admin/paytech-faq/_/import-markdown",
        { markdown },
      );
      setImportResult({ open: true, ...data });
      toast.success(`Imported · created ${data.created.length} · updated ${data.updated.length} · soft-deleted ${data.soft_deleted.length}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  const onExport = async () => {
    try {
      const { data } = await api.get("/admin/paytech-faq/_/export-markdown");
      await navigator.clipboard.writeText(data.markdown);
      toast.success(`Markdown copied (${data.count} entries).`);
    } catch (e) {
      toast.error("Could not copy markdown.");
    }
  };

  const onConfirmRemoval = async (faqId) => {
    try {
      await api.post(`/admin/paytech-faq/${faqId}/confirm-removal`);
      toast.success("Removal confirmed.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not confirm.");
    }
  };

  const onRestore = async (faqId) => {
    try {
      await api.post(`/admin/paytech-faq/${faqId}/restore`);
      toast.success("Restored.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restore.");
    }
  };

  return (
    <div data-testid="paytech-faq-page" className="max-w-6xl space-y-6 pb-16">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <BookOpen className="h-4 w-4 text-cyan-300/80" />
            <h1 className="text-[18px] tracking-wide text-white">PayTech FAQ</h1>
          </div>
          <p className="mt-2 text-[12.5px] text-zinc-400 max-w-2xl">
            Curated answers Nerea serves to merchants for PayTech API
            questions. High-confidence matches (≥ 0.80) short-circuit the
            brain. Inactive entries are kept (audit + restore) but never
            served.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onExport} data-testid="faq-export-markdown" className="inline-flex items-center gap-1.5 rounded-md border border-white/[0.10] px-3 py-1.5 text-[11.5px] text-zinc-300 hover:bg-white/[0.04]">
            <Download className="h-3 w-3" /> Export markdown
          </button>
          <button onClick={() => setImportResult({ open: true })} data-testid="faq-open-import" className="inline-flex items-center gap-1.5 rounded-md border border-white/[0.10] px-3 py-1.5 text-[11.5px] text-zinc-300 hover:bg-white/[0.04]">
            <Upload className="h-3 w-3" /> Import markdown
          </button>
          <button onClick={() => setEditing({})} data-testid="faq-new" className="inline-flex items-center gap-1.5 rounded-md border border-cyan-400/30 bg-cyan-400/[0.08] px-3 py-1.5 text-[11.5px] text-cyan-100 hover:bg-cyan-400/[0.14]">
            <Plus className="h-3 w-3" /> New entry
          </button>
        </div>
      </header>

      {pending.length > 0 && (
        <div data-testid="faq-pending-removals-banner" className="rounded-xl border border-amber-400/30 bg-amber-400/[0.06] p-4 text-amber-100">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4" />
            <div className="text-[13px]">
              {pending.length} entries removed from source markdown — review before they stay inactive
            </div>
          </div>
          <ul className="space-y-1.5 text-[12.5px]">
            {pending.map((e) => (
              <li key={e.faq_id} className="flex flex-wrap items-center justify-between gap-2">
                <span className="truncate">
                  <span className="text-zinc-500 font-mono mr-1">{e.faq_id}</span>
                  · {e.topic} · {e.question}
                </span>
                <span className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => onRestore(e.faq_id)} data-testid={`faq-restore-${e.faq_id}`} className="inline-flex items-center gap-1 rounded border border-emerald-400/30 bg-emerald-400/[0.06] px-2 py-0.5 text-[10.5px] text-emerald-100">
                    <RotateCcw className="h-3 w-3" /> Restore
                  </button>
                  <button onClick={() => onConfirmRemoval(e.faq_id)} data-testid={`faq-confirm-removal-${e.faq_id}`} className="inline-flex items-center gap-1 rounded border border-rose-400/30 bg-rose-400/[0.06] px-2 py-0.5 text-[10.5px] text-rose-100">
                    <Check className="h-3 w-3" /> Confirm removal
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {topics.map((t) => (
          <button
            key={t}
            onClick={() => setTopic(t)}
            data-testid={`faq-topic-${t}`}
            className={`rounded-full border px-3 py-1 text-[11.5px] ${topic === t ? "border-white/[0.20] bg-white/[0.08] text-white" : "border-white/[0.08] bg-white/[0.02] text-zinc-400 hover:bg-white/[0.04]"}`}
          >
            {t}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <label className="text-[11.5px] text-zinc-400 flex items-center gap-1.5">
            <input type="checkbox" data-testid="faq-active-only" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
            Active only
          </label>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-zinc-500" />
            <input
              type="text"
              placeholder="Search Q/A…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="faq-search"
              className="rounded-md border border-white/[0.08] bg-black/30 pl-7 pr-2 py-1 text-[12px] text-white"
            />
          </div>
          <button onClick={refresh} data-testid="faq-refresh" className="inline-flex items-center gap-1 text-[11.5px] text-zinc-400 hover:text-white">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {error && <div data-testid="faq-error" className="rounded-xl border border-rose-400/20 bg-rose-400/[0.03] p-4 text-rose-200 text-[12.5px]">{error}</div>}

      {entries.length === 0 && !loading && (
        <div data-testid="faq-empty" className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-6 text-zinc-500 text-[12.5px]">
          No entries match the current filter.
        </div>
      )}

      <ul className="space-y-2">
        {entries.map((e) => (
          <FAQRow
            key={e.faq_id}
            entry={e}
            onEdit={() => setEditing(e)}
            onToggle={() => onToggle(e.faq_id)}
            onDelete={() => onDelete(e.faq_id)}
            onShowHistory={() => setHistoryFor(e)}
          />
        ))}
      </ul>

      {editing && <EditModal entry={editing} onClose={() => setEditing(null)} onSaved={refresh} />}
      {historyFor && <HistoryDrawer entry={historyFor} onClose={() => setHistoryFor(null)} />}
      {importResult?.open && <ImportModal onClose={() => setImportResult(null)} onImport={onImport} importing={importing} lastResult={importResult.created !== undefined ? importResult : null} />}
    </div>
  );
}

function FAQRow({ entry, onEdit, onToggle, onDelete, onShowHistory }) {
  return (
    <li
      data-testid={`faq-row-${entry.faq_id}`}
      className={`rounded-xl border ${entry.active ? "border-white/[0.06] bg-white/[0.015]" : "border-zinc-700/40 bg-zinc-800/[0.18]"} p-4`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center rounded-full border ${entry.active ? "border-emerald-400/30 bg-emerald-400/[0.06] text-emerald-100" : "border-zinc-500/30 bg-zinc-500/[0.08] text-zinc-400"} px-2 py-0.5 text-[10.5px]`}>
              {entry.active ? "active" : "inactive"}
            </span>
            <span className="rounded border border-white/[0.08] bg-white/[0.02] px-2 py-0.5 text-[10.5px] text-zinc-400 font-mono">
              {entry.topic}
            </span>
            <span className="text-[10.5px] text-zinc-500 font-mono">v{entry.version} · {entry.faq_id}</span>
          </div>
          <div className="mt-2 text-[13px] text-zinc-100 leading-snug">{entry.question}</div>
          <div className="mt-1 text-[12px] text-zinc-400 whitespace-pre-wrap break-words">{entry.answer}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button data-testid={`faq-edit-${entry.faq_id}`} onClick={onEdit} className="inline-flex items-center gap-1 rounded-md border border-white/[0.10] px-2 py-1 text-[10.5px] text-zinc-300 hover:bg-white/[0.04]">
            <Pencil className="h-3 w-3" /> Edit
          </button>
          {entry.version > 1 && (
            <button data-testid={`faq-history-${entry.faq_id}`} onClick={onShowHistory} className="inline-flex items-center gap-1 rounded-md border border-white/[0.10] px-2 py-1 text-[10.5px] text-zinc-300 hover:bg-white/[0.04]">
              <History className="h-3 w-3" /> v{entry.version}
            </button>
          )}
          <button data-testid={`faq-toggle-${entry.faq_id}`} onClick={onToggle} className="inline-flex items-center gap-1 rounded-md border border-white/[0.10] px-2 py-1 text-[10.5px] text-zinc-300 hover:bg-white/[0.04]">
            {entry.active ? <ToggleRight className="h-3 w-3 text-emerald-300" /> : <ToggleLeft className="h-3 w-3 text-zinc-500" />}
          </button>
          <button data-testid={`faq-delete-${entry.faq_id}`} onClick={onDelete} className="inline-flex items-center gap-1 rounded-md border border-rose-400/20 px-2 py-1 text-[10.5px] text-rose-200 hover:bg-rose-400/[0.06]">
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
    </li>
  );
}

function EditModal({ entry, onClose, onSaved }) {
  const isCreate = !entry.faq_id;
  const [question, setQuestion] = useState(entry.question || "");
  const [answer, setAnswer] = useState(entry.answer || "");
  const [topic, setTopic] = useState(entry.topic || "auth");
  const [active, setActive] = useState(entry.active !== false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!question.trim() || !answer.trim()) {
      toast.error("Question + answer required.");
      return;
    }
    setSaving(true);
    try {
      if (isCreate) {
        await api.post("/admin/paytech-faq", { question, answer, topic, active });
        toast.success("Entry created.");
      } else {
        await api.patch(`/admin/paytech-faq/${entry.faq_id}`, { question, answer, topic, active });
        toast.success("Entry updated.");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="faq-edit-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-2xl border border-white/[0.10] bg-zinc-950 p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-[14px] text-white">{isCreate ? "New FAQ entry" : `Edit ${entry.faq_id}`}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <Field label="Question">
          <textarea data-testid="faq-edit-question" className="w-full rounded-md border border-white/[0.08] bg-black/30 p-2 text-[13px] text-white min-h-[60px]" value={question} onChange={(e) => setQuestion(e.target.value)} />
        </Field>
        <Field label="Answer">
          <textarea data-testid="faq-edit-answer" className="w-full rounded-md border border-white/[0.08] bg-black/30 p-2 text-[13px] text-white min-h-[140px]" value={answer} onChange={(e) => setAnswer(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Topic">
            <select data-testid="faq-edit-topic" className="w-full rounded-md border border-white/[0.08] bg-black/30 p-2 text-[13px] text-white" value={topic} onChange={(e) => setTopic(e.target.value)}>
              {TOPICS.filter((t) => t !== "all").map((t) => (<option key={t} value={t}>{t}</option>))}
            </select>
          </Field>
          <label className="flex items-center gap-2 text-[12.5px] text-zinc-300 pt-6">
            <input type="checkbox" data-testid="faq-edit-active" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Active
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button onClick={onClose} className="rounded-md border border-white/[0.10] px-3 py-1.5 text-[12px] text-zinc-300 hover:bg-white/[0.04]">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="faq-edit-save" className="inline-flex items-center gap-1.5 rounded-md border border-cyan-400/30 bg-cyan-400/[0.08] px-3 py-1.5 text-[12px] text-cyan-100 hover:bg-cyan-400/[0.14] disabled:opacity-50">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function HistoryDrawer({ entry, onClose }) {
  return (
    <div data-testid="faq-history-drawer" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-2xl border border-white/[0.10] bg-zinc-950 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[14px] text-white">Version history · {entry.faq_id}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <ul className="space-y-3 text-[12.5px]">
          {[...(entry.version_history || [])].reverse().map((h) => (
            <li key={h.version} className="rounded-md border border-white/[0.06] bg-white/[0.01] p-3">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">v{h.version} · {h.edited_at} · {h.edited_by_email || h.edited_by_user_id}</div>
              <div className="mt-1 text-zinc-400 italic">{h.diff_summary}</div>
              <details className="mt-2">
                <summary className="cursor-pointer text-zinc-300">show content</summary>
                <div className="mt-2 text-zinc-200"><span className="text-zinc-500">Q: </span>{h.question}</div>
                <div className="mt-1 text-zinc-200 whitespace-pre-wrap break-words"><span className="text-zinc-500">A: </span>{h.answer}</div>
              </details>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ImportModal({ onClose, onImport, importing, lastResult }) {
  const [md, setMd] = useState("");
  return (
    <div data-testid="faq-import-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-2xl border border-white/[0.10] bg-zinc-950 p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-[14px] text-white">Import markdown</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        {/* neptune-allow: literal filename paytech_api_faq_v1.md displayed verbatim to the operator as an import-format hint */}
        <p className="text-[12px] text-zinc-400">
          Paste paytech_api_faq_v1.md content. Existing entries match by inline
          <code className="mx-1 text-[11px] font-mono">&lt;!-- faq_id: ... --&gt;</code>
          comments. Entries absent from the markdown will be soft-deleted with a
          review banner before they stay inactive.
        </p>
        <textarea data-testid="faq-import-textarea" className="w-full rounded-md border border-white/[0.08] bg-black/30 p-3 text-[12px] text-white font-mono min-h-[280px]" value={md} onChange={(e) => setMd(e.target.value)} placeholder="## auth&#10;*Q:* How do I authenticate?&#10;*A:* Use the API key from the dashboard.&#10;" />
        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-white/[0.10] px-3 py-1.5 text-[12px] text-zinc-300 hover:bg-white/[0.04]">Cancel</button>
          <button onClick={() => onImport(md)} disabled={importing || !md.trim()} data-testid="faq-import-submit" className="inline-flex items-center gap-1.5 rounded-md border border-cyan-400/30 bg-cyan-400/[0.08] px-3 py-1.5 text-[12px] text-cyan-100 disabled:opacity-50">
            {importing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            Import
          </button>
        </div>
        {lastResult && (
          <div data-testid="faq-import-result" className="mt-3 rounded-md border border-emerald-400/25 bg-emerald-400/[0.04] p-3 text-[12px]">
            <div className="text-emerald-100 mb-1">Import done</div>
            <div className="text-zinc-400">created: {lastResult.created.length} · updated: {lastResult.updated.length} · unchanged: {lastResult.unchanged.length} · soft-deleted: {lastResult.soft_deleted.length}</div>
            <details className="mt-2">
              <summary className="cursor-pointer text-zinc-300">show updated markdown (paste back into source file)</summary>
              <pre className="mt-2 rounded border border-white/[0.06] bg-black/30 p-2 text-[11px] text-zinc-300 overflow-x-auto whitespace-pre-wrap">{lastResult.updated_markdown}</pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}
